import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

export const AnimatedExpandingButton = ({ 
  onClick, 
  disabled = false, 
  className = '', 
  text = '',
  icon = null, 
  textColor = 'text-[#0056d6]',
}) => {
  const buttonRef = useRef(null);
  const [expandedWidth, setExpandedWidth] = useState(150);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (buttonRef.current) {
      const textWidth = text.length * 6;
      setExpandedWidth(32 + textWidth + 16);
    }
  }, [text]);

  const handleMouseDown = (e) => {
    e.preventDefault();
  };

  const defaultIcon = (
    <svg 
      className="w-4 h-4 fill-black transition-all duration-200 ease-in-out group-hover:fill-[#0056d6] group-focus:fill-[#0056d6] group-hover:rotate-180 group-focus:rotate-180"
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 30 30"
    >
        <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z" />
    </svg>
  );

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      disabled={disabled}
      tabIndex={-1}
      type="button"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      onMouseDown={handleMouseDown}
      className={`group relative flex items-center ${isHovered ? 'justify-start' : 'justify-center'} bg-transparent rounded-md overflow-hidden h-8 transition-all duration-300 
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} 
        ${className}
        w-8 hover:w-auto hover:px-2 focus:outline-none active:outline-none focus-visible:outline-none`}
      style={{ 
        width: isHovered ? `${expandedWidth}px` : '32px',
        outline: 'none !important',
        boxShadow: 'none !important',
        WebkitTapHighlightColor: 'transparent',
        border: '0.2px solid #3f1057'
      }}
    >
      <div 
        className="absolute top-0 right-0 w-0 h-0 border-solid border-r-white border-t-transparent border-l-transparent border-b-transparent transition-all duration-300 ease-in-out"
        style={{
          borderRightWidth: isHovered ? `${expandedWidth * 2}px` : '12px',
          borderBottomWidth: isHovered ? '64px' : '12px'
        }}
      ></div>
      
      <div className={`flex items-center justify-center w-4 h-4 z-10 transition-all duration-300 ${isHovered ? 'ml-0' : ''}`}>
        {icon || defaultIcon}
      </div>
      
      <span className={`whitespace-nowrap overflow-hidden max-w-0 group-hover:max-w-xs ml-0 group-hover:ml-2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 ${textColor} text-xs`}>
        {text}
      </span>
    </button>
  );
};


export const Button = ({
  children,
  type = 'button',
  variant = 'primary',
  onClick,
  className = '',
  disabled = false,
  icon = null
}) => {
  const getButtonClasses = () => {
    const baseClasses =
      'relative overflow-hidden rounded-2xl px-8 py-2 text-sm font-semibold focus:outline-none active:outline-none focus-visible:outline-none transition-all duration-200 flex items-center justify-center gap-2';

    const variants = {
      animated: `
        border-[0.2px] border-[#875792] text-[#3f1057] cursor-pointer
        group hover:border-[#3f1057]
      `,
      primary: `
        bg-[#3f1057] text-white hover:bg-[#5a167a] transition-colors duration-200
      `,
      secondary: `
        text-[#3f1057] hover:bg-gray-100 transition-colors duration-200
      `,
      outline: `
        border-[1.5px] border-[#3f1057] text-[#3f1057] bg-transparent
        hover:bg-gray-100 transition-colors duration-150
      `,
    };

    return `${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`;
  };

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled}
      initial="rest"
      whileHover="hover"
      animate="rest"
      className={getButtonClasses()}
    >
      {variant === 'animated' && (
        <>
          <motion.span
            variants={{
              rest: { x: '-100%', y: '100%' },
              hover: { x: '0%', y: '0%' },
            }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="absolute left-0 top-0 h-full w-full bg-[#3f1057] z-0 rounded-xl"
            style={{ transformOrigin: 'left' }}
          />
          <motion.span
            variants={{
              rest: { color: '#3f1057' },
              hover: { color: '#ffffff' },
            }}
            transition={{ duration: 0.3 }}
            className="relative z-10 flex items-center gap-1"
          >
            {icon}
            {children}
          </motion.span>
        </>
      )}

      {/* Secondary & Outline */}
      {variant !== 'animated' && (
        <span className="relative z-10 flex items-center gap-1">
          {icon}
          {children}
        </span>
      )}
    </motion.button>
  );
};

export default Button;